import { useCallback, useEffect, useState, type FormEvent } from "react";
import { api, formatApiError } from "../lib/api";
import { ListSkeleton } from "../components/Skeleton";

type Role = "Admin" | "Contributor" | "Auditor_Viewer";

interface MemberSummary {
  uuid: string;
  fullName: string;
  email: string;
  role: Role;
  status: "active" | "pending";
  invitedAt: string | null;
  acceptedAt: string | null;
  isErased: boolean;
}

const ROLE_LABEL: Record<Role, string> = {
  Admin: "Admin",
  Contributor: "Contributor",
  Auditor_Viewer: "Auditor (read-only)",
};

const formatDateUk = (iso: string | null): string => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yyyy = d.getFullYear();
  return `${dd}/${mm}/${yyyy}`;
};

export function Members() {
  const [members, setMembers] = useState<MemberSummary[] | null>(null);
  const [errors, setErrors] = useState<string[]>([]);
  const [info, setInfo] = useState<string | null>(null);

  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState<Role>("Contributor");
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const res = await api<{ members: MemberSummary[] }>("/memberships");
      setMembers(res.members);
    } catch (err) {
      setErrors(formatApiError(err));
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const submitInvite = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setErrors([]);
    setInfo(null);
    try {
      const result = await api<{ status: string; userExists: boolean }>("/memberships/invite", {
        method: "POST",
        body: { email: inviteEmail.toLowerCase().trim(), role: inviteRole },
      });
      if (result.status === "invited") {
        setInfo(`Invitation sent. They will see it on their dashboard next time they sign in.`);
      } else if (result.status === "already-pending") {
        setInfo("That user already has a pending invitation to this organisation.");
      } else if (result.status === "already-member") {
        setInfo("That user is already an active member of this organisation.");
      }
      setInviteEmail("");
      await refresh();
    } catch (err) {
      setErrors(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const removeMember = async (uuid: string, fullName: string) => {
    setBusy(true);
    setErrors([]);
    setInfo(null);
    try {
      await api(`/memberships/${uuid}`, { method: "DELETE" });
      setInfo(`Removed ${fullName} from the organisation.`);
      await refresh();
    } catch (err) {
      setErrors(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-slate-900 dark:text-slate-100">Members</h1>
        <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
          People with access to this organisation. Admins manage members, Contributors edit content,
          Auditor (read-only) sees only Authorised evidence and the audit log.
        </p>
      </div>

      <section className="rounded-lg border border-slate-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-800">
        <h2 className="text-sm font-semibold text-slate-900 dark:text-slate-100">
          Invite by email
        </h2>
        <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
          The person needs an account first — ask them to sign up at the login page, then invite
          them here. They will see a pending invitation banner when they sign in.
        </p>
        <form onSubmit={submitInvite} className="mt-3 grid gap-3 sm:grid-cols-[1fr_180px_120px]">
          <input
            type="email"
            required
            placeholder="email@example.com"
            value={inviteEmail}
            onChange={(e) => setInviteEmail(e.target.value)}
            aria-label="Email address"
            aria-invalid={errors.length > 0 || undefined}
            aria-describedby={errors.length > 0 ? "members-errors" : undefined}
            className="rounded-md border border-slate-300 bg-white px-3 py-2 text-sm shadow-sm focus:border-brand-500 focus:ring-brand-500 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100"
          />
          <select
            value={inviteRole}
            onChange={(e) => setInviteRole(e.target.value as Role)}
            className="rounded-md border border-slate-300 bg-white px-3 py-2 text-sm shadow-sm dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100"
          >
            <option value="Admin">Admin</option>
            <option value="Contributor">Contributor</option>
            <option value="Auditor_Viewer">Auditor (read-only)</option>
          </select>
          <button
            type="submit"
            disabled={busy}
            className="rounded-md bg-brand-600 px-3 py-2 text-sm font-semibold text-white hover:bg-brand-700 disabled:opacity-60"
          >
            {busy ? "Sending…" : "Send invite"}
          </button>
        </form>
      </section>

      {errors.length > 0 ? (
        <div
          id="members-errors"
          role="alert"
          className="rounded border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-950/40 dark:text-red-200"
        >
          {errors[0]}
        </div>
      ) : null}
      {info ? (
        <div
          role="status"
          className="rounded border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-800 dark:border-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-200"
        >
          {info}
        </div>
      ) : null}

      <section>
        <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
          Current members
        </h2>
        {members === null ? (
          <ListSkeleton rows={3} />
        ) : (
          <div className="mt-2 overflow-hidden rounded-lg border border-slate-200 dark:border-slate-700">
            <table className="w-full text-left text-sm">
              <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500 dark:bg-slate-900 dark:text-slate-400">
                <tr>
                  <th className="px-4 py-2">Name</th>
                  <th className="px-4 py-2">Role</th>
                  <th className="px-4 py-2">Status</th>
                  <th className="px-4 py-2">Joined</th>
                  <th className="px-4 py-2"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200 bg-white dark:divide-slate-700 dark:bg-slate-800">
                {members.map((m) => (
                  <tr key={m.uuid}>
                    <td className="px-4 py-2 align-top">
                      <p className="font-medium text-slate-900 dark:text-slate-100">{m.fullName}</p>
                      <p className="text-xs text-slate-500 dark:text-slate-400">{m.email}</p>
                    </td>
                    <td className="px-4 py-2 align-top text-xs text-slate-700 dark:text-slate-300">
                      {ROLE_LABEL[m.role]}
                    </td>
                    <td className="px-4 py-2 align-top text-xs text-slate-700 dark:text-slate-300">
                      {m.status === "active" ? "Active" : "Pending"}
                    </td>
                    <td className="px-4 py-2 align-top text-xs text-slate-700 dark:text-slate-300">
                      {formatDateUk(m.acceptedAt) === "—" && m.invitedAt
                        ? `Invited ${formatDateUk(m.invitedAt)}`
                        : formatDateUk(m.acceptedAt)}
                    </td>
                    <td className="px-4 py-2 align-top text-right">
                      <button
                        type="button"
                        onClick={() => void removeMember(m.uuid, m.fullName)}
                        disabled={busy}
                        className="rounded border border-red-200 px-2 py-0.5 text-xs font-medium text-red-700 hover:bg-red-50 disabled:opacity-60 dark:border-red-800 dark:text-red-200 dark:hover:bg-red-950/40"
                      >
                        Remove
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
